/*
 * WorkAndBuhin.vue の created/re-init 時の初期ロード、復元、初期選択判定を切り出した処理。
 */
import { WorkBuhinInputContext } from "@/components/work-buhin/WorkBuhinAdapter";
import { hasCachedKatashikiForUketsuke } from "@/components/work-buhin/WorkBuhinNisseiren";
import { KatashikiParams } from "@/model/KatashikiParams";
import {
  createSingleRecommendWork,
  isRecommendInspectType,
  shouldUseSingleRecommendWork
} from "@/components/work-buhin/WorkBuhinWorkSelection";
import { Buhin, BuhinCategory } from "@/model/Buhin";

export type SetBuhinCateActionType =
  | "normalWork"
  | "manualWork"
  | "singleRecommendWork"
  | "recommendData"
  | "keepBuhinMd15";

export interface SetBuhinCateAction {
  type: SetBuhinCateActionType;
  work: any | null;
  index: number;
  isManualWork: boolean;
  filteredNormalBuhinList: any[] | null;
}

export const SET_BUHIN_CATE_KEEP_MD15_DELAY_MS = 2000;

export interface SetBuhinCateEffectState {
  shouldApplyFilteredNormalBuhinList: boolean;
  normalWorkTarget: any;
  filteredNormalBuhinList: any[];
  shouldSelectWork: boolean;
  selectWork: any;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  shouldDeepCopySelectWork: boolean;
  forceSelectWork: boolean;
  shouldSetRecommendSelectionState: boolean;
  activeWorkIndex: number;
  isManualWork: boolean;
  shouldInitListSelectedBuhin: boolean;
  initListSelectedBuhinWork: any;
  shouldSetOsusumeBuhinList: boolean;
  osusumeBuhinList: any[];
  shouldSelectOsusumeTab: boolean;
  shouldSelectBuhinShubetsu: boolean;
  buhinShubetsuCd: string;
  buhinBunruiList: any;
  shouldKeepBuhinMd15WithDelay: boolean;
  keepBuhinMd15DelayMs: number;
}

export interface SetBuhinCateFilteredNormalBuhinApplyState {
  shouldApply: boolean;
  normalWorkTarget: any;
  filteredNormalBuhinList: any[];
}

export interface SetBuhinCateSelectWorkEffectState {
  shouldSelectWork: boolean;
  shouldDeepCopySelectWork: boolean;
  selectWork: any;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  forceSelectWork: boolean;
}

export interface SetBuhinCateDelayedKeepMd15EffectState {
  shouldKeepBuhinMd15: boolean;
}

export interface SetBuhinCateRecommendSelectionEffectState {
  shouldApply: boolean;
  activeWorkIndex: number;
  isManualWork: boolean;
  shouldInitListSelectedBuhin: boolean;
  initListSelectedBuhinWork: any;
  shouldSetOsusumeBuhinList: boolean;
  osusumeBuhinList: any[];
  shouldSelectOsusumeTab: boolean;
  shouldSelectBuhinShubetsu: boolean;
  buhinShubetsuCd: string;
  buhinBunruiList: any;
}

export interface ReInitBuhinState {
  shouldSetActiveSeibiId: boolean;
  activeSeibiId: any;
  shouldKeepBuhinMd15: boolean;
}

export const RE_INIT_BUHIN_DELAY_MS = 300;

export type ReInitBuhinConfirmRestoreType = "md15" | "osusume";

export interface ReInitBuhinDelayedAction {
  confirmRestoreType: ReInitBuhinConfirmRestoreType;
  confirmRestoreData: any;
  confirmRestoreWork: any;
  selectWork: any;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  forceSelectWork: boolean;
  shouldForceUpdate: boolean;
}

export interface ReInitBuhinDelayedConfirmEffectState {
  shouldKeepBuhinMd15: boolean;
  keepBuhinMd15Data: any;
  shouldKeepBuhinConfirmOsusume: boolean;
  keepBuhinConfirmOsusumeWork: any;
}

export interface ReInitBuhinDelayedSelectionEffectState {
  shouldSelectWork: boolean;
  selectWork: any;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  forceSelectWork: boolean;
  shouldForceUpdate: boolean;
}

export interface CreatedKatashikiState {
  hasKatashiki: boolean;
  shouldSearchKatashiki: boolean;
  shouldClearKatashikiState: boolean;
}

export interface CreatedKatashikiApplyEffectState {
  hasKatashiki: boolean;
  shouldSearchKatashiki: boolean;
  shouldClearKatashikiState: boolean;
  shouldLoadExtendsImageInspect: boolean;
  shouldUpdateKatashikiDisplay: boolean;
}

export type CreatedConfirmRestoreType = "none" | "normal" | "osusume";

export interface CreatedConfirmRestoreAction {
  type: CreatedConfirmRestoreType;
  seibiList: any;
  manualSeibiList: any;
  data: any;
  isInit: boolean;
}

export interface CreatedConfirmRestoreEffectState {
  shouldKeepBuhinConfirm: boolean;
  shouldKeepBuhinConfirmOsusume: boolean;
  seibiList: any;
  manualSeibiList: any;
  osusumeData: any;
  isInit: boolean;
}

export interface CreatedBuhinMasterLoadActionState {
  shouldRequestBuhinMaster: boolean;
  shouldSetLoadingBuhin: boolean;
  shouldUseCachedBuhinMaster: boolean;
}

export type CreatedBuhinMasterLoadApplyEffectState =
  CreatedBuhinMasterLoadActionState;

export interface CreatedBuhinMasterResponseApplyState {
  shouldApplyBuhinMaster: boolean;
  buhinMaster: Buhin[];
}

export interface CreatedBuhinMasterResponseApplyEffectState {
  shouldApplyBuhinMaster: boolean;
  buhinMaster: Buhin[];
  shouldSetBuhinCate: boolean;
}

export interface CreatedBuhinMasterErrorApplyEffectState {
  isBuhinError: boolean;
  shouldHideBuhin: boolean;
}

export interface CreatedBuhinMasterFinallyApplyEffectState {
  loadingBuhin: boolean;
}

export interface CreatedNisseirenLoadEffectState {
  shouldLoadNisseirenJunsei: boolean;
}

export interface SeibiDisableSaveUpdate {
  seibi: any;
  payload: any;
  disabledSave: boolean;
}

export interface SeibiDisableSaveApplyEffectState {
  shouldApply: boolean;
  updates: SeibiDisableSaveUpdate[];
}

/*
 * resolveCreatedKatashikiState を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function resolveCreatedKatashikiState(params: {
  katashikiParams: Partial<KatashikiParams>;
  uketsukeExtendsId: number;
}): CreatedKatashikiState {
  const hasKatashiki = hasCachedKatashikiForUketsuke(
    params.katashikiParams,
    params.uketsukeExtendsId
  );
  return {
    hasKatashiki,
    shouldSearchKatashiki: !hasKatashiki,
    shouldClearKatashikiState: !hasKatashiki
  };
}

/*
 * buildCreatedKatashikiApplyEffectState を WorkAndBuhin.vue から分離し、
 * created 時の型式 state 反映、検索、clear、表示更新の実行条件を固定するため。
 */
export function buildCreatedKatashikiApplyEffectState(
  katashikiState: CreatedKatashikiState
): CreatedKatashikiApplyEffectState {
  return {
    hasKatashiki: katashikiState.hasKatashiki,
    shouldSearchKatashiki: katashikiState.shouldSearchKatashiki,
    shouldClearKatashikiState: katashikiState.shouldClearKatashikiState,
    shouldLoadExtendsImageInspect: true,
    shouldUpdateKatashikiDisplay: true
  };
}

/*
 * resolveCreatedDisableSaveTarget を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function resolveCreatedDisableSaveTarget(data: any): any[] | null {
  if (data && data.seibiList && data.seibiList.length > 0) {
    return data.seibiList;
  }
  if (data && data.manualSeibiList && data.manualSeibiList.length > 0) {
    return data.manualSeibiList;
  }
  return null;
}

/*
 * resolveCreatedConfirmRestoreAction を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function resolveCreatedConfirmRestoreAction(params: {
  data: any;
  isDialog: boolean;
}): CreatedConfirmRestoreAction {
  if (!params.isDialog) {
    return {
      type: "none",
      seibiList: null,
      manualSeibiList: null,
      data: null,
      isInit: false
    };
  }

  if (params.data && params.data.seibiList) {
    if (params.data.workBuhinSelected > 1) {
      return {
        type: "normal",
        seibiList: params.data.seibiList,
        manualSeibiList: params.data.manualSeibiList,
        data: null,
        isInit: false
      };
    }
    return {
      type: "none",
      seibiList: null,
      manualSeibiList: null,
      data: null,
      isInit: false
    };
  }

  return {
    type: "osusume",
    seibiList: null,
    manualSeibiList: null,
    data: params.data,
    isInit: true
  };
}

/*
 * buildCreatedConfirmRestoreEffectState を WorkAndBuhin.vue から分離し、
 * created 時の confirm 復元 action type と component 側で実行する副作用の対応を固定するため。
 */
export function buildCreatedConfirmRestoreEffectState(
  action: CreatedConfirmRestoreAction
): CreatedConfirmRestoreEffectState {
  return {
    shouldKeepBuhinConfirm: action.type === "normal",
    shouldKeepBuhinConfirmOsusume: action.type === "osusume",
    seibiList: action.seibiList,
    manualSeibiList: action.manualSeibiList,
    osusumeData: action.data,
    isInit: action.isInit
  };
}

/*
 * shouldLoadBuhinMaster を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function shouldLoadBuhinMaster(listBuhinData: Buhin[] | null): boolean {
  return !listBuhinData || listBuhinData.length === 0;
}

/*
 * buildCreatedBuhinMasterLoadActionState を WorkAndBuhin.vue から分離し、
 * created 時に部品マスタを API 取得するか、既存データで初期選択へ進むかを固定するため。
 */
export function buildCreatedBuhinMasterLoadActionState(
  listBuhinData: Buhin[] | null
): CreatedBuhinMasterLoadActionState {
  const shouldRequestBuhinMaster = shouldLoadBuhinMaster(listBuhinData);
  return {
    shouldRequestBuhinMaster,
    shouldSetLoadingBuhin: shouldRequestBuhinMaster,
    shouldUseCachedBuhinMaster: !shouldRequestBuhinMaster
  };
}

/*
 * buildCreatedBuhinMasterLoadApplyEffectState を WorkAndBuhin.vue から分離し、
 * created 時の部品マスタ API 実行、loading、cached 初期化の副作用条件を固定するため。
 */
export function buildCreatedBuhinMasterLoadApplyEffectState(
  actionState: CreatedBuhinMasterLoadActionState
): CreatedBuhinMasterLoadApplyEffectState {
  return {
    shouldRequestBuhinMaster: actionState.shouldRequestBuhinMaster,
    shouldSetLoadingBuhin: actionState.shouldSetLoadingBuhin,
    shouldUseCachedBuhinMaster: actionState.shouldUseCachedBuhinMaster
  };
}

/*
 * buildCreatedBuhinMasterResponseApplyState を WorkAndBuhin.vue から分離し、
 * 部品マスタ API 成功時に保存と初期選択へ進めるレスポンス条件を固定するため。
 */
export function buildCreatedBuhinMasterResponseApplyState(
  response: Buhin[] | null
): CreatedBuhinMasterResponseApplyState {
  const shouldApplyBuhinMaster = !!(response && response.length > 0);
  const buhinMaster = shouldApplyBuhinMaster && response ? response : [];
  return {
    shouldApplyBuhinMaster,
    buhinMaster
  };
}

/*
 * buildCreatedBuhinMasterResponseApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品マスタ API 成功後の store 保存と部品カテゴリ初期化条件を固定するため。
 */
export function buildCreatedBuhinMasterResponseApplyEffectState(
  applyState: CreatedBuhinMasterResponseApplyState
): CreatedBuhinMasterResponseApplyEffectState {
  return {
    shouldApplyBuhinMaster: applyState.shouldApplyBuhinMaster,
    buhinMaster: applyState.buhinMaster,
    shouldSetBuhinCate: applyState.shouldApplyBuhinMaster
  };
}

/*
 * buildCreatedBuhinMasterErrorApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品マスタ API 失敗時の error 表示と部品画面非表示条件を固定するため。
 */
export function buildCreatedBuhinMasterErrorApplyEffectState():
  CreatedBuhinMasterErrorApplyEffectState {
  return {
    isBuhinError: true,
    shouldHideBuhin: true
  };
}

/*
 * buildCreatedBuhinMasterFinallyApplyEffectState を WorkAndBuhin.vue から分離し、
 * 部品マスタ API 完了時に loadingBuhin を戻す値を固定するため。
 */
export function buildCreatedBuhinMasterFinallyApplyEffectState():
  CreatedBuhinMasterFinallyApplyEffectState {
  return {
    loadingBuhin: false
  };
}

/*
 * shouldLoadNisseirenAfterCreated を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function shouldLoadNisseirenAfterCreated(params: {
  hasKatashiki: boolean;
  isUseNisseiren: number;
  hideNisseirenJunsei: boolean;
}): boolean {
  return !!(
    params.hasKatashiki &&
    params.isUseNisseiren &&
    !params.hideNisseirenJunsei
  );
}

/*
 * buildCreatedNisseirenLoadEffectState を WorkAndBuhin.vue から分離し、
 * created の部品マスタ初期化後に日整連/純正ロードへ進む条件を固定するため。
 */
export function buildCreatedNisseirenLoadEffectState(params: {
  hasKatashiki: boolean;
  isUseNisseiren: number;
  hideNisseirenJunsei: boolean;
}): CreatedNisseirenLoadEffectState {
  return {
    shouldLoadNisseirenJunsei: shouldLoadNisseirenAfterCreated(params)
  };
}

/*
 * shouldInitSeibiDisableSave を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function shouldInitSeibiDisableSave(data: any): boolean {
  return !!(
    data &&
    data.sknInspectType != null &&
    data.sknInspectType <= 1
  );
}

/*
 * buildSeibiDisableSavePayload を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function buildSeibiDisableSavePayload(
  seibi: any,
  data: any
): any {
  return {
    seibiDetailId: seibi.seibiDetailId,
    sknUketsukeInspectId: data.sknUketsukeInspectId
  };
}

/*
 * buildSeibiDisableSaveUpdates を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function buildSeibiDisableSaveUpdates(params: {
  data: any;
  seibiList: any[];
  isSeibiDetailIdExist: (payload: any) => boolean;
}): SeibiDisableSaveUpdate[] {
  if (!shouldInitSeibiDisableSave(params.data)) {
    return [];
  }
  return (params.seibiList || []).map((seibi: any) => {
    const payload = buildSeibiDisableSavePayload(seibi, params.data);
    return {
      seibi,
      payload,
      disabledSave: params.isSeibiDetailIdExist(payload)
    };
  });
}

/*
 * buildSeibiDisableSaveApplyEffectState を WorkAndBuhin.vue から分離し、
 * 初期化時に seibi.disabledSave へ反映する更新対象と値を固定するため。
 */
export function buildSeibiDisableSaveApplyEffectState(
  updates: SeibiDisableSaveUpdate[]
): SeibiDisableSaveApplyEffectState {
  return {
    shouldApply: updates.length > 0,
    updates
  };
}

/*
 * getLatestBuhinCategory を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function getLatestBuhinCategory(listBuhinData: Buhin[] | null): BuhinCategory[] {
  if (!listBuhinData || listBuhinData.length === 0) {
    return [];
  }
  const index = listBuhinData.length - 1;
  return listBuhinData[index].items;
}

/*
 * resolveSetBuhinCateAction を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function resolveSetBuhinCateAction(params: {
  data: any;
  context: WorkBuhinInputContext;
  editSeibi: boolean;
}): SetBuhinCateAction {
  if (!params.data || Object.keys(params.data).length === 0) {
    return {
      type: "keepBuhinMd15",
      work: null,
      index: -1,
      isManualWork: false,
      filteredNormalBuhinList: null
    };
  }

  const context = params.context;
  if (context.seibiList.length > 0 && context.manualSeibiList.length === 0) {
    const seibiList = params.data.seibiList || context.seibiList;
    const seibiIndex = context.selectedWorkIndex;
    const work = context.activeWork || seibiList[seibiIndex];
    const filteredNormalBuhinList = (work.buhinList || []).filter(
      (buhin: any) => buhin.isDelete != 1
    );
    return {
      type: "normalWork",
      work,
      index: seibiIndex,
      isManualWork: false,
      filteredNormalBuhinList
    };
  }

  if (context.manualSeibiList.length > 0) {
    return {
      type: "manualWork",
      work: context.activeWork || params.data.manualSeibiList[0],
      index: context.selectedWorkIndex,
      isManualWork: true,
      filteredNormalBuhinList: null
    };
  }

  if (shouldUseSingleRecommendWork(params.data, params.editSeibi)) {
    return {
      type: "singleRecommendWork",
      work: createSingleRecommendWork(params.data),
      index: 0,
      isManualWork: true,
      filteredNormalBuhinList: null
    };
  }

  return {
    type: "recommendData",
    work: params.data,
    index: 0,
    isManualWork: false,
    filteredNormalBuhinList: null
  };
}

/*
 * buildSetBuhinCateEffectState を WorkAndBuhin.vue から分離し、
 * setBuhinCate の action type と component 側で実行する初期選択副作用の対応を固定するため。
 */
export function buildSetBuhinCateEffectState(params: {
  action: SetBuhinCateAction;
  data: any;
  context: WorkBuhinInputContext;
}): SetBuhinCateEffectState {
  const action = params.action;
  const seibiList = params.data && params.data.seibiList
    ? params.data.seibiList
    : params.context.seibiList;
  const normalWorkTarget =
    action.type === "normalWork" && seibiList ? seibiList[action.index] : null;
  const baseState: SetBuhinCateEffectState = {
    shouldApplyFilteredNormalBuhinList: false,
    normalWorkTarget,
    filteredNormalBuhinList: [],
    shouldSelectWork: false,
    selectWork: null,
    selectWorkIndex: action.index,
    selectWorkIsManual: action.isManualWork,
    shouldDeepCopySelectWork: false,
    forceSelectWork: true,
    shouldSetRecommendSelectionState: false,
    activeWorkIndex: action.index,
    isManualWork: action.isManualWork,
    shouldInitListSelectedBuhin: false,
    initListSelectedBuhinWork: null,
    shouldSetOsusumeBuhinList: false,
    osusumeBuhinList: [],
    shouldSelectOsusumeTab: false,
    shouldSelectBuhinShubetsu: false,
    buhinShubetsuCd: "",
    buhinBunruiList: null,
    shouldKeepBuhinMd15WithDelay: false,
    keepBuhinMd15DelayMs: SET_BUHIN_CATE_KEEP_MD15_DELAY_MS
  };
  if (action.type === "normalWork") {
    return {
      ...baseState,
      shouldApplyFilteredNormalBuhinList: true,
      filteredNormalBuhinList: action.filteredNormalBuhinList || [],
      shouldSelectWork: true,
      selectWork: normalWorkTarget,
      selectWorkIsManual: false
    };
  }
  if (action.type === "manualWork" || action.type === "singleRecommendWork") {
    return {
      ...baseState,
      shouldSelectWork: true,
      selectWork: action.work,
      shouldDeepCopySelectWork: true
    };
  }
  if (action.type === "recommendData") {
    return {
      ...baseState,
      shouldSetRecommendSelectionState: true,
      shouldInitListSelectedBuhin: true,
      initListSelectedBuhinWork: params.data,
      shouldSetOsusumeBuhinList: params.context.osusumeBuhinList.length > 0,
      osusumeBuhinList: params.context.osusumeBuhinList,
      shouldSelectOsusumeTab: params.context.osusumeBuhinList.length > 0,
      shouldSelectBuhinShubetsu: params.context.osusumeBuhinList.length === 0,
      buhinShubetsuCd: params.data && params.data.buhinShubetsuCd,
      buhinBunruiList: params.data && params.data.buhinBunruiList
    };
  }
  if (action.type === "keepBuhinMd15") {
    return {
      ...baseState,
      shouldKeepBuhinMd15WithDelay: true
    };
  }
  return baseState;
}

/*
 * buildSetBuhinCateFilteredNormalBuhinApplyState を WorkAndBuhin.vue から分離し、
 * 通常作業の削除済み部品除外済みリストを作業へ反映する条件と対象を固定するため。
 */
export function buildSetBuhinCateFilteredNormalBuhinApplyState(
  effectState: SetBuhinCateEffectState
): SetBuhinCateFilteredNormalBuhinApplyState {
  return {
    shouldApply: effectState.shouldApplyFilteredNormalBuhinList,
    normalWorkTarget: effectState.normalWorkTarget,
    filteredNormalBuhinList: effectState.filteredNormalBuhinList
  };
}

/*
 * buildSetBuhinCateSelectWorkEffectState を WorkAndBuhin.vue から分離し、
 * setBuhinCate で初期選択する作業と deepCopy 要否、onSelectWork 引数を固定するため。
 */
export function buildSetBuhinCateSelectWorkEffectState(
  effectState: SetBuhinCateEffectState
): SetBuhinCateSelectWorkEffectState {
  return {
    shouldSelectWork: effectState.shouldSelectWork,
    shouldDeepCopySelectWork: effectState.shouldDeepCopySelectWork,
    selectWork: effectState.selectWork,
    selectWorkIndex: effectState.selectWorkIndex,
    selectWorkIsManual: effectState.selectWorkIsManual,
    forceSelectWork: effectState.forceSelectWork
  };
}

/*
 * buildSetBuhinCateRecommendSelectionEffectState を WorkAndBuhin.vue から分離し、
 * おすすめ整備データ時に作業選択状態と部品タブ初期表示へ進む条件を固定するため。
 */
export function buildSetBuhinCateRecommendSelectionEffectState(
  effectState: SetBuhinCateEffectState
): SetBuhinCateRecommendSelectionEffectState {
  if (!effectState.shouldSetRecommendSelectionState) {
    return {
      shouldApply: false,
      activeWorkIndex: effectState.activeWorkIndex,
      isManualWork: effectState.isManualWork,
      shouldInitListSelectedBuhin: false,
      initListSelectedBuhinWork: null,
      shouldSetOsusumeBuhinList: false,
      osusumeBuhinList: [],
      shouldSelectOsusumeTab: false,
      shouldSelectBuhinShubetsu: false,
      buhinShubetsuCd: "",
      buhinBunruiList: null
    };
  }
  return {
    shouldApply: true,
    activeWorkIndex: effectState.activeWorkIndex,
    isManualWork: effectState.isManualWork,
    shouldInitListSelectedBuhin: effectState.shouldInitListSelectedBuhin,
    initListSelectedBuhinWork: effectState.initListSelectedBuhinWork,
    shouldSetOsusumeBuhinList: effectState.shouldSetOsusumeBuhinList,
    osusumeBuhinList: effectState.osusumeBuhinList,
    shouldSelectOsusumeTab: effectState.shouldSelectOsusumeTab,
    shouldSelectBuhinShubetsu: effectState.shouldSelectBuhinShubetsu,
    buhinShubetsuCd: effectState.buhinShubetsuCd,
    buhinBunruiList: effectState.buhinBunruiList
  };
}

/*
 * buildSetBuhinCateDelayedKeepMd15EffectState を WorkAndBuhin.vue から分離し、
 * setBuhinCate の delay 後に MD15 部品 confirm 復元へ進む条件を固定するため。
 */
export function buildSetBuhinCateDelayedKeepMd15EffectState(
  effectState: SetBuhinCateEffectState
): SetBuhinCateDelayedKeepMd15EffectState {
  return {
    shouldKeepBuhinMd15: effectState.shouldKeepBuhinMd15WithDelay
  };
}

/*
 * resolveReInitBuhinState を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function resolveReInitBuhinState(work: any): ReInitBuhinState {
  if (work && work.seibiDetailId) {
    return {
      shouldSetActiveSeibiId: true,
      activeSeibiId: work.seibiDetailId,
      shouldKeepBuhinMd15: isRecommendInspectType(work.sknInspectType)
    };
  }
  return {
    shouldSetActiveSeibiId: false,
    activeSeibiId: null,
    shouldKeepBuhinMd15: false
  };
}

/*
 * buildReInitBuhinDelayedAction を WorkAndBuhin.vue から分離し、
 * reInitBuhin の 300ms delay 後に行う復元順序を親側置換や単体テストで固定するため。
 */
export function buildReInitBuhinDelayedAction(params: {
  data: any;
  work: any;
  reInitState: ReInitBuhinState;
  activeWorkIndex: number;
  isManualWork: boolean;
}): ReInitBuhinDelayedAction {
  return {
    confirmRestoreType: params.reInitState.shouldKeepBuhinMd15 ? "md15" : "osusume",
    confirmRestoreData: params.data,
    confirmRestoreWork: params.work,
    selectWork: params.work,
    selectWorkIndex: params.activeWorkIndex,
    selectWorkIsManual: params.isManualWork,
    forceSelectWork: true,
    shouldForceUpdate: true
  };
}

/*
 * buildReInitBuhinDelayedConfirmEffectState を WorkAndBuhin.vue から分離し、
 * reInitBuhin の delay 後に MD15 とおすすめのどちらで confirm 復元するかを固定するため。
 */
export function buildReInitBuhinDelayedConfirmEffectState(
  delayedAction: ReInitBuhinDelayedAction
): ReInitBuhinDelayedConfirmEffectState {
  const shouldKeepBuhinMd15 = delayedAction.confirmRestoreType === "md15";
  return {
    shouldKeepBuhinMd15,
    keepBuhinMd15Data: delayedAction.confirmRestoreData,
    shouldKeepBuhinConfirmOsusume: !shouldKeepBuhinMd15,
    keepBuhinConfirmOsusumeWork: delayedAction.confirmRestoreWork
  };
}

/*
 * buildReInitBuhinDelayedSelectionEffectState を WorkAndBuhin.vue から分離し、
 * reInitBuhin の delay 後に再選択と再描画を行う条件と引数を固定するため。
 */
export function buildReInitBuhinDelayedSelectionEffectState(
  delayedAction: ReInitBuhinDelayedAction
): ReInitBuhinDelayedSelectionEffectState {
  return {
    shouldSelectWork: !!delayedAction.selectWork,
    selectWork: delayedAction.selectWork,
    selectWorkIndex: delayedAction.selectWorkIndex,
    selectWorkIsManual: delayedAction.selectWorkIsManual,
    forceSelectWork: delayedAction.forceSelectWork,
    shouldForceUpdate: delayedAction.shouldForceUpdate
  };
}

/*
 * findInitialSelectedSeibiIndex を WorkAndBuhin.vue から分離し、
 * 初期ロードと初期選択 を親側置換や単体テストで固定するため。
 */
export function findInitialSelectedSeibiIndex(params: {
  isManualWork: boolean;
  data: any;
}): number {
  if (params.isManualWork || !params.data || !params.data.seibiList) {
    return -1;
  }
  for (let i = 0; i < params.data.seibiList.length; i++) {
    if (params.data.seibiList[i].buhinSelected > 0) {
      return i;
    }
  }
  return -1;
}

export interface InitialSelectSeibiActionState {
  shouldSelectWork: boolean;
  work: any;
  index: number;
  isManualWork: boolean;
}

/*
 * buildInitialSelectSeibiActionState を WorkAndBuhin.vue から分離し、
 * 初期表示時に選択済み作業へ onSelectWork する条件と引数を親側置換や単体テストで固定するため。
 */
export function buildInitialSelectSeibiActionState(params: {
  isManualWork: boolean;
  data: any;
}): InitialSelectSeibiActionState {
  const index = findInitialSelectedSeibiIndex(params);
  if (index === -1) {
    return {
      shouldSelectWork: false,
      work: null,
      index,
      isManualWork: false
    };
  }
  return {
    shouldSelectWork: true,
    work: params.data.seibiList[index],
    index,
    isManualWork: false
  };
}
